import isUndefined from "lodash/isUndefined";
import get from "lodash/get";
import reverse from "lodash/reverse";
import map from "lodash/map";
import fromPairs from "lodash/fromPairs";

import { underscored } from "underscore.string";

export const MOBILE = "mobile";
export const MOBILE_PORTRAIT = "mobile-portrait";
export const XS = "xs";
export const SM = "sm";
export const SM_PLUS = "sm-plus";
export const MD = "md";
export const MD_PLUS = "md-plus";
export const LG = "lg";

export const Definitions = [
  {
    breakpoint: LG,
    minWidth: 1440,
  },
  {
    breakpoint: MD_PLUS,
    minWidth: 1280,
  },
  {
    breakpoint: MD,
    minWidth: 940,
  },
  {
    breakpoint: SM_PLUS,
    minWidth: 700,
  },
  {
    breakpoint: SM,
    minWidth: 568,
    minHeight: 280,
  },
  {
    breakpoint: XS,
    minWidth: 380,
  },
  {
    breakpoint: MOBILE_PORTRAIT,
    maxWidth: 379, // Must be XS#minWidth - 1
    portrait: true,
  },
  {
    breakpoint: MOBILE,
  },
].map((breakpoint) => ({
  ...breakpoint,
  isGreaterThan(compareBreakpoint) {
    const compareIndex = Definitions.indexOf(compareBreakpoint);
    const thisIndex = Definitions.indexOf(this);

    if (compareIndex === -1) {
      throw new TypeError("Could not find compareBreakpoint");
    }

    return compareIndex > thisIndex;
  },
  isGreaterThanOrEquals(compareBreakpoint) {
    const compareIndex = Definitions.indexOf(compareBreakpoint);
    const thisIndex = Definitions.indexOf(this);

    if (compareIndex === -1) {
      throw new TypeError("Could not find compareBreakpoint");
    }

    return compareIndex >= thisIndex;
  },
  isLessThan(compareBreakpoint) {
    const compareIndex = Definitions.indexOf(compareBreakpoint);
    const thisIndex = Definitions.indexOf(this);

    return compareIndex < thisIndex;
  },
  isLessThanOrEquals(compareBreakpoint) {
    const compareIndex = Definitions.indexOf(compareBreakpoint);
    const thisIndex = Definitions.indexOf(this);

    return compareIndex <= thisIndex;
  },
}));

export const BreakpointDefinition = Definitions.reduce(
  (memo, definition) => ({
    ...memo,
    [underscored(definition.breakpoint).toUpperCase()]: definition,
  }),
  {},
);

// Generate a media query to be used in CSS.
export function generateMediaQuery({
  maxWidth,
  maxHeight,
  minWidth,
  minHeight,
  portrait,
}) {
  const conditions = [];

  if (
    isUndefined(maxHeight) &&
    isUndefined(maxWidth) &&
    isUndefined(minHeight) &&
    isUndefined(minWidth) &&
    isUndefined(portrait)
  ) {
    conditions.push("min-width: 0");
  } else {
    if (maxWidth) {
      conditions.push(`max-width: ${maxWidth}px`);
    }

    if (maxHeight) {
      conditions.push(`max-height: ${maxHeight}px`);
    }

    if (minWidth) {
      conditions.push(`min-width: ${minWidth}px`);
    }

    if (minHeight) {
      conditions.push(`min-height: ${minHeight}px`);
    }

    if (portrait) {
      conditions.push("orientation: portrait");
    }
  }

  return `(${conditions.sort().join(") and (")})`;
}

// Generate all media queries.
export function generateMediaQueries() {
  return fromPairs(
    map(reverse(Definitions), (definition) => [
      `--viewport-${definition.breakpoint}`,
      generateMediaQuery(definition),
    ]),
  );
}

// Define Breakpoint enum.
const Breakpoint = Object.freeze({
  MOBILE,
  MOBILE_PORTRAIT,
  XS,
  SM,
  SM_PLUS,
  MD,
  MD_PLUS,
  LG,
});
export default Breakpoint;

export const BreakpointOrder = [...Definitions]
  .reverse()
  .map(({ breakpoint }) => breakpoint);

export class BreakpointError extends Error {}

// Take a `width` and `height` and return if the breakpoint definition in
// `definition` matches. This tries to mimic the inheritance we'll see in
// css. For example, Breakpoint.LG will match everything that Breakpoint.SM
// and Breakpoint.MD will match.
export function sizeMatchesDefinition({ width, height, definition }) {
  if (height > get(definition, "maxHeight", Infinity)) {
    return false;
  }
  if (width > get(definition, "maxWidth", Infinity)) {
    return false;
  }
  if (width < get(definition, "minWidth", -Infinity)) {
    return false;
  }
  if (height < get(definition, "minHeight", -Infinity)) {
    return false;
  }
  if (definition.portrait && height < width) {
    return false;
  }

  return true;
}

export function getBreakpoint({ height, width }) {
  if (height < 0) {
    throw new TypeError("height must be > 0");
  } else if (width < 0) {
    throw new TypeError("width must be > 0");
  }

  return Definitions.find((definition) =>
    sizeMatchesDefinition({ width, height, definition }),
  ).breakpoint;
}

export function getBreakpointDefinition(size) {
  return Definitions.find(
    ({ breakpoint }) => breakpoint === getBreakpoint(size),
  );
}
